export type AccountSessionUser = {
  id: string;
  handle?: string;
  pseudonym?: string;
  status?: string;
  publicProfileCreated?: false;
  publicAuthorExposed?: false;
};

export type AccountSessionMeResponse = {
  ok: true;
  user: AccountSessionUser;
};

export type AccountSessionClientOptions = {
  apiBase?: string;
  fetchImpl?: typeof fetch;
  abortSignal?: AbortSignal;
};

function joinApiUrl(apiBase: string, path: string): string {
  const base = String(apiBase || '').replace(/\/$/, '');
  return `${base}${path}`;
}

function errorFromAccountSessionResponse(status: number, data: unknown): Error {
  const payload = data as { error?: { code?: string }; errorCode?: string; code?: string } | null;
  const error = new Error(
    payload?.error?.code || payload?.errorCode || payload?.code || 'ACCOUNT_SESSION_FAILED',
  );
  (error as Error & { status?: number; payload?: unknown }).status = status;
  (error as Error & { status?: number; payload?: unknown }).payload = data;
  return error;
}

function toAccountSessionUser(account: unknown): AccountSessionUser | null {
  if (!account || typeof account !== 'object') return null;
  const source = account as {
    id?: unknown;
    handle?: unknown;
    pseudonym?: unknown;
    status?: unknown;
    publicProfileCreated?: unknown;
    publicAuthorExposed?: unknown;
  };
  if (typeof source.id !== 'string' || !source.id) return null;
  return {
    id: source.id,
    ...(typeof source.handle === 'string' ? { handle: source.handle } : {}),
    ...(typeof source.pseudonym === 'string' ? { pseudonym: source.pseudonym } : {}),
    ...(typeof source.status === 'string' ? { status: source.status } : {}),
    publicProfileCreated: false,
    publicAuthorExposed: false,
  };
}

export async function fetchAccountSessionMe(
  options: AccountSessionClientOptions = {},
): Promise<AccountSessionMeResponse | null> {
  const fetcher = options.fetchImpl ?? fetch;
  const response = await fetcher(joinApiUrl(options.apiBase || '', '/api/account/me'), {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'include',
    signal: options.abortSignal,
  });
  const data = await response.json().catch(() => null);
  if (response.status === 401) return null;
  if (!response.ok || !data?.ok) throw errorFromAccountSessionResponse(response.status, data);
  const user = toAccountSessionUser((data as { account?: unknown }).account);
  return user ? { ok: true, user } : null;
}
